import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid2 as Grid,
  Typography,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  LinearProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Assignment,
  Build,
  AccountBalance,
  Notifications,
  TrendingUp,
  Warning,
  CheckCircle,
  Schedule,
  AttachMoney,
  Message,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ppMockApi } from "../../../../services/ppMockApi";

const PpDashboardPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession]?.user);

  const [dashboardData, setDashboardData] = useState({
    submissions: { total: 0, due75: 0, due50: 0, due25: 0, pastDue: 0 },
    inspections: { total: 0, pending: 0, approved: 0, failed: 0 },
    finance: { monthlyEarnings: 0, pendingPayouts: 0 },
    notifications: [],
    recentActivity: [],
    messaging: { unreadMessages: 0, totalConversations: 0, recentMessages: [] },
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);

        // Load all dashboard data
        const [submissions, inspections, finance, notifications] =
          await Promise.all([
            ppMockApi.getSubmissions(),
            ppMockApi.getInspectionsSummary(),
            ppMockApi.getFinanceSummary(),
            ppMockApi.getNotifications(),
          ]);

        setDashboardData({
          submissions: {
            total: submissions.length,
            due75: submissions.filter((s) => s.dueBucket === "75%").length,
            due50: submissions.filter((s) => s.dueBucket === "50%").length,
            due25: submissions.filter((s) => s.dueBucket === "25%").length,
            pastDue: submissions.filter((s) => s.dueBucket === "past_due")
              .length,
          },
          inspections: {
            total: inspections.total,
            pending: inspections.pending,
            approved: inspections.approved,
            failed: inspections.failed,
          },
          finance: finance,
          notifications: notifications.filter((n) => !n.read).slice(0, 5),
          recentActivity: submissions.slice(0, 5), // Mock recent activity
          messaging: {
            unreadMessages: 3,
            totalConversations: 8,
            recentMessages: [
              {
                id: "MSG001",
                sender: "John Smith, PE",
                preview:
                  "Please review the updated plans for submission PRS001",
                timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
                unread: true,
              },
              {
                id: "MSG002",
                sender: "City Planning Dept",
                preview: "Inspection scheduled for tomorrow at 2 PM",
                timestamp: new Date(
                  Date.now() - 2 * 60 * 60 * 1000
                ).toISOString(),
                unread: true,
              },
              {
                id: "MSG003",
                sender: "Mike Johnson",
                preview: "Thanks for the quick turnaround on the permit",
                timestamp: new Date(
                  Date.now() - 4 * 60 * 60 * 1000
                ).toISOString(),
                unread: false,
              },
            ],
          },
        });
      } catch (error) {
        console.error("Failed to load dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const StatCard = ({
    title,
    value,
    subtitle,
    icon,
    color = "primary",
    onClick,
  }) => (
    <Card
      sx={{
        cursor: onClick ? "pointer" : "default",
        "&:hover": onClick ? { elevation: 3 } : {},
      }}
      onClick={onClick}
    >
      <CardContent>
        <Grid container alignItems="center" spacing={2}>
          <Grid size={{ xs: 8 }}>
            <Typography variant="h6" color="text.secondary">
              {title}
            </Typography>
            <Typography variant="h4" color={`${color}.main`}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Grid>
          <Grid size={{ xs: 4 }} sx={{ textAlign: "right" }}>
            <Avatar sx={{ bgcolor: `${color}.main`, ml: "auto" }}>
              {icon}
            </Avatar>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const getDueBadgeColor = (bucket) => {
    switch (bucket) {
      case "75%":
        return "success";
      case "50%":
        return "warning";
      case "25%":
        return "error";
      case "past_due":
        return "error";
      default:
        return "default";
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;

    return time.toLocaleDateString();
  };

  if (loading) {
    return (
      <Box sx={{ width: "100%", mt: 0 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" color="primary" sx={{ mb: 3 }}>
        Private Provider Dashboard
      </Typography>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Active Submissions"
            value={dashboardData.submissions.total}
            subtitle="Plan Review Requests"
            icon={<Assignment />}
            color="primary"
            onClick={() => navigate("/pp/submissions")}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Pending Inspections"
            value={dashboardData.inspections.pending}
            subtitle="Awaiting Approval"
            icon={<Build />}
            color="warning"
            onClick={() => navigate("/pp/inspections")}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Monthly Earnings"
            value={`$${dashboardData.finance.monthlyEarnings?.toLocaleString() || 0}`}
            subtitle="This month"
            icon={<AttachMoney />}
            color="success"
            onClick={() => navigate("/pp/finance/payouts")}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Unread Notifications"
            value={dashboardData.notifications.length}
            subtitle="Require attention"
            icon={<Notifications />}
            color="error"
            onClick={() => navigate("/pp/notifications")}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Due Submissions Breakdown */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Submissions by Due Status
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Chip
                      label="75%"
                      color={getDueBadgeColor("75%")}
                      size="small"
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${dashboardData.submissions.due75} submissions`}
                    secondary="Due in more than 3 days"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip
                      label="50%"
                      color={getDueBadgeColor("50%")}
                      size="small"
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${dashboardData.submissions.due50} submissions`}
                    secondary="Due in 2-3 days"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip
                      label="25%"
                      color={getDueBadgeColor("25%")}
                      size="small"
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${dashboardData.submissions.due25} submissions`}
                    secondary="Due in 1 day"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Warning color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${dashboardData.submissions.pastDue} submissions`}
                    secondary="Past due"
                  />
                </ListItem>
              </List>
              <Button
                variant="outlined"
                fullWidth
                sx={{ mt: 2 }}
                onClick={() => navigate("/pp/submissions")}
              >
                View All Submissions
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Recent Activity
              </Typography>
              <List>
                {dashboardData.recentActivity.map((activity, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      {activity.status === "completed" ? (
                        <CheckCircle color="success" />
                      ) : (
                        <Schedule color="warning" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={`Submission #${activity.id}`}
                      secondary={`${activity.address} - ${activity.status}`}
                    />
                  </ListItem>
                ))}
              </List>
              <Button
                variant="outlined"
                fullWidth
                sx={{ mt: 2 }}
                onClick={() => navigate("/pp/submissions")}
              >
                View Recent Activity
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Messaging Overview */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">Recent Messages</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip
                    label={`${dashboardData.messaging.unreadMessages} unread`}
                    color="error"
                    size="small"
                  />
                  <Chip
                    label={`${dashboardData.messaging.totalConversations} conversations`}
                    color="primary"
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </Box>
              <List>
                {dashboardData.messaging.recentMessages.map(
                  (message, index) => (
                    <ListItem key={message.id} sx={{ px: 0 }}>
                      <ListItemIcon>
                        <Avatar
                          sx={{
                            bgcolor: message.unread
                              ? "primary.main"
                              : "grey.300",
                            width: 32,
                            height: 32,
                          }}
                        >
                          <Message fontSize="small" />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight={message.unread ? "bold" : "normal"}
                            >
                              {message.sender}
                            </Typography>
                            {message.unread && (
                              <Chip label="New" size="small" color="error" />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "200px",
                              }}
                            >
                              {message.preview}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatTimeAgo(message.timestamp)}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  )
                )}
              </List>
              <Button
                variant="outlined"
                fullWidth
                sx={{ mt: 2 }}
                startIcon={<Message />}
                onClick={() => navigate("/pp/messaging")}
              >
                View All Messages
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Quick Actions
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<Assignment />}
                    onClick={() => navigate("/pp/submissions")}
                  >
                    Review Submissions
                  </Button>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<Build />}
                    onClick={() => navigate("/pp/inspections")}
                  >
                    Manage Inspections
                  </Button>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<AccountBalance />}
                    onClick={() => navigate("/pp/finance")}
                  >
                    View Finance
                  </Button>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<Message />}
                    onClick={() => navigate("/pp/messaging")}
                  >
                    Messaging
                  </Button>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<Notifications />}
                    onClick={() => navigate("/pp/notifications")}
                  >
                    Notifications
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PpDashboardPage;
